import type { Contact, ContactMessage, Message } from '@doneverse/shared'
import { api } from '../api/index.js'

export const chatService = {
  // Contact Fetching
  getContacts: async () => {
    return api.get('/v1/contacts').json<Contact[]>()
  },

  // Message Fetching — `before`/`limit` page backwards through history
  // (cursor = the oldest loaded message's created_at).
  getMessages: async (contactId: string, options: { limit?: number; before?: string } = {}) => {
    const params = new URLSearchParams()
    if (options.limit !== undefined) params.set('limit', String(options.limit))
    if (options.before) params.set('before', options.before)
    const qs = params.toString()
    const path = qs ? `/v1/contacts/${contactId}/messages?${qs}` : `/v1/contacts/${contactId}/messages`
    return api.get(path).json<ContactMessage[]>()
  },

  // Message Sending
  sendMessage: async (
    contactId: string,
    payload: {
      content?: string
      attachment_url?: string
      attachment_name?: string
      attachment_content_type?: string
      attachment_size_bytes?: number
      /** Quote an earlier message in this same conversation. */
      reply_to_message_id?: string
    },
  ) => {
    await api.post({ contact_id: contactId, ...payload }, '/v1/messages').json()
  },

  // Message Management
  markAsRead: async (messageIds: string[]) => {
    if (messageIds.length === 0) return
    await api.post({ message_ids: messageIds }, '/v1/messages/ack').json()
  },

  // Mark conversation as read (updates project_read_state for unread count)
  markContactAsRead: async (contactId: string) => {
    await api.post({ contact_id: contactId }, '/v1/messages/read').json()
  },

  fetchUndelivered: async () => {
    return api.get('/v1/messages/undelivered').json<Message[]>()
  },

  // Message Reactions
  addMessageReaction: async (messageId: string, emoji: string) => {
    await api.post({ emoji }, `/v1/messages/${messageId}/reactions`).res()
  },

  removeMessageReaction: async (messageId: string, emoji: string) => {
    await api.delete(`/v1/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`).res()
  },
}
